import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { MATTERS, REVIEW_QUEUE } from '../lib/mock-data';
import { AlertCircle, BrainCircuit, Search, Briefcase, CheckCircle2, TrendingUp, AlertTriangle, Play, FileSearch, Stethoscope, ShieldAlert, Activity, BarChart3, Files, Database, ServerCrash, ChevronRight, UserPlus, Mail, FolderTree, Settings2, ListTodo } from 'lucide-react';
import { PersonaId } from '../../App';
interface DashboardProps {
  onNavigate: (page: string) => void;
  persona?: PersonaId | null;
}
export function Dashboard({
  onNavigate,
  persona = 'EF'
}: DashboardProps) {
  // Extended mock data for the new dashboard sections
  const ALERTS = [{
    id: 1,
    type: 'warning',
    message: 'Low OCR quality detected in "Ramirez_Medical_Records_Part2.pdf" (42% confidence).',
    matter: 'Ramirez v. Lone Star Freight'
  }, {
    id: 2,
    type: 'error',
    message: 'Ingestion paused for source: "Castillo Dropbox Integration" - Authentication failed.',
    matter: 'Castillo v. Westfield Properties'
  }, {
    id: 3,
    type: 'info',
    message: 'Duplicate document cluster detected in recent production set.',
    matter: 'Garcia v. Metro Transit'
  }, {
    id: 4,
    type: 'warning',
    message: 'Classification confidence below threshold (65%) for 14 documents.',
    matter: 'Ortega v. Gulf Coast Industrial'
  }];
  const INGESTION_ACTIVITY = [{
    id: 1,
    batch: 'Production Set A',
    docs: 452,
    status: 'Completed',
    time: '10 mins ago',
    type: 'Pleadings & Discovery'
  }, {
    id: 2,
    batch: 'Dr. Smith Records',
    docs: 84,
    status: 'Processing',
    time: 'Active',
    type: 'Medical Records'
  }, {
    id: 3,
    batch: 'Client Intake Packet',
    docs: 12,
    status: 'Completed',
    time: '1 hour ago',
    type: 'Intake Forms'
  }, {
    id: 4,
    batch: 'Opposing Counsel Email',
    docs: 3,
    status: 'Failed',
    time: '2 hours ago',
    type: 'Correspondence'
  }];
  const RECENT_ANSWERS = [{
    id: 1,
    query: 'What date did the plaintiff first complain of L4-L5 pain?',
    matter: 'Ramirez v. Lone Star Freight',
    confidence: 98,
    time: '1 hour ago'
  }, {
    id: 2,
    query: "Summarize Dr. Allen's opinion on the mechanism of injury.",
    matter: 'Garcia v. Metro Transit Authority',
    confidence: 92,
    time: '3 hours ago'
  }, {
    id: 3,
    query: 'List all instances of prior back injuries mentioned in the medical records.',
    matter: "Nguyen v. St. Luke's Medical Center",
    confidence: 85,
    time: 'Yesterday'
  }];
  const DOC_TYPES = [{
    type: 'Medical Records',
    count: 5420,
    percentage: 45
  }, {
    type: 'Discovery Responses',
    count: 2840,
    percentage: 24
  }, {
    type: 'Pleadings',
    count: 1560,
    percentage: 13
  }, {
    type: 'Correspondence',
    count: 1200,
    percentage: 10
  }, {
    type: 'Expert Reports',
    count: 980,
    percentage: 8
  }];
  const getGreeting = () => {
    switch (persona) {
      case 'EF':
        return 'Welcome back, Edward.';
      case 'AT':
        return 'Welcome back, Ana.';
      case 'MS':
        return 'Welcome back, Maria.';
      case 'RK':
        return 'Welcome back, Robert.';
      default:
        return 'Welcome back.';
    }
  };
  const getSubtitle = () => {
    switch (persona) {
      case 'EF':
        return 'Firm intelligence, critical risks, and active operations.';
      case 'AT':
        return 'Your pending reviews, chronology tasks, and validations.';
      case 'MS':
        return 'Intake queue, new matters, and routing status.';
      case 'RK':
        return 'System health, ingestion queues, and workflow exceptions.';
      default:
        return 'Firm intelligence, ingestion health, and active operations.';
    }
  };
  // Render different quick actions based on persona
  const renderQuickActions = () => {
    if (persona === 'MS') {
      return <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-606">
          <Button variant="outline" className="h-auto py-4 flex flex-col items-center justify-center gap-2 bg-white hover:bg-slate-50 hover:border-primary/30 transition-all" onClick={() => onNavigate('matters')} data-id="element-607">

            <div className="p-2 bg-emerald-50 text-emerald-600 rounded-full" data-id="element-608">
              <UserPlus className="w-5 h-5" data-id="element-609" />
            </div>
            <span className="font-medium" data-id="element-610">New Matter Intake</span>
          </Button>
          <Button variant="outline" className="h-auto py-4 flex flex-col items-center justify-center gap-2 bg-white hover:bg-slate-50 hover:border-primary/30 transition-all" onClick={() => onNavigate('workflows')} data-id="element-611">

            <div className="p-2 bg-blue-50 text-blue-600 rounded-full" data-id="element-612">
              <Mail className="w-5 h-5" data-id="element-613" />
            </div>
            <span className="font-medium" data-id="element-614">Process Email Queue</span>
          </Button>
          <Button variant="outline" className="h-auto py-4 flex flex-col items-center justify-center gap-2 bg-white hover:bg-slate-50 hover:border-primary/30 transition-all" onClick={() => onNavigate('workflows')} data-id="element-615">

            <div className="p-2 bg-slate-100 text-slate-600 rounded-full" data-id="element-616">
              <FolderTree className="w-5 h-5" data-id="element-617" />
            </div>
            <span className="font-medium" data-id="element-618">Setup Folders</span>
          </Button>
          <Button variant="outline" className="h-auto py-4 flex flex-col items-center justify-center gap-2 bg-white hover:bg-slate-50 hover:border-primary/30 transition-all" onClick={() => onNavigate('search')} data-id="element-619">

            <div className="p-2 bg-purple-50 text-purple-600 rounded-full" data-id="element-620">
              <Search className="w-5 h-5" data-id="element-621" />
            </div>
            <span className="font-medium" data-id="element-622">Conflict Check</span>
          </Button>
        </div>;
    }
    if (persona === 'AT') {
      return <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-623">
          <Button variant="outline" className="h-auto py-4 flex flex-col items-center justify-center gap-2 bg-white hover:bg-slate-50 hover:border-primary/30 transition-all" onClick={() => onNavigate('chronology')} data-id="element-624">

            <div className="p-2 bg-blue-50 text-blue-600 rounded-full" data-id="element-625">
              <Stethoscope className="w-5 h-5" data-id="element-626" />
            </div>
            <span className="font-medium" data-id="element-627">Resume Chronology</span>
          </Button>
          <Button variant="outline" className="h-auto py-4 flex flex-col items-center justify-center gap-2 bg-white hover:bg-amber-50 hover:border-amber-300 transition-all border-amber-200" onClick={() => onNavigate('review')} data-id="element-628">

            <div className="p-2 bg-amber-100 text-amber-600 rounded-full" data-id="element-629">
              <ListTodo className="w-5 h-5" data-id="element-630" />
            </div>
            <span className="font-medium text-amber-900" data-id="element-631">
              My Review Tasks (4)
            </span>
          </Button>
          <Button variant="outline" className="h-auto py-4 flex flex-col items-center justify-center gap-2 bg-white hover:bg-slate-50 hover:border-primary/30 transition-all" onClick={() => onNavigate('search')} data-id="element-632">

            <div className="p-2 bg-slate-100 text-slate-600 rounded-full" data-id="element-633">
              <FileSearch className="w-5 h-5" data-id="element-634" />
            </div>
            <span className="font-medium" data-id="element-635">Search Medical Records</span>
          </Button>
          <Button variant="outline" className="h-auto py-4 flex flex-col items-center justify-center gap-2 bg-white hover:bg-slate-50 hover:border-primary/30 transition-all" onClick={() => onNavigate('matters')} data-id="element-636">

            <div className="p-2 bg-emerald-50 text-emerald-600 rounded-full" data-id="element-637">
              <CheckCircle2 className="w-5 h-5" data-id="element-638" />
            </div>
            <span className="font-medium" data-id="element-639">Validate Extraction</span>
          </Button>
        </div>;
    }
    if (persona === 'RK') {
      return <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-640">
          <Button variant="outline" className="h-auto py-4 flex flex-col items-center justify-center gap-2 bg-white hover:bg-slate-50 hover:border-primary/30 transition-all" onClick={() => onNavigate('workflows')} data-id="element-641">

            <div className="p-2 bg-slate-100 text-slate-600 rounded-full" data-id="element-642">
              <Settings2 className="w-5 h-5" data-id="element-643" />
            </div>
            <span className="font-medium" data-id="element-644">Manage Workflows</span>
          </Button>
          <Button variant="outline" className="h-auto py-4 flex flex-col items-center justify-center gap-2 bg-white hover:bg-slate-50 hover:border-primary/30 transition-all" onClick={() => onNavigate('admin')} data-id="element-645">

            <div className="p-2 bg-blue-50 text-blue-600 rounded-full" data-id="element-646">
              <Database className="w-5 h-5" data-id="element-647" />
            </div>
            <span className="font-medium" data-id="element-648">Ingestion Status</span>
          </Button>
          <Button variant="outline" className="h-auto py-4 flex flex-col items-center justify-center gap-2 bg-white hover:bg-red-50 hover:border-red-300 transition-all border-red-200" onClick={() => onNavigate('admin')} data-id="element-649">

            <div className="p-2 bg-red-100 text-red-600 rounded-full" data-id="element-650">
              <ServerCrash className="w-5 h-5" data-id="element-651" />
            </div>
            <span className="font-medium text-red-900" data-id="element-652">OCR Exceptions (3)</span>
          </Button>
          <Button variant="outline" className="h-auto py-4 flex flex-col items-center justify-center gap-2 bg-white hover:bg-slate-50 hover:border-primary/30 transition-all" onClick={() => onNavigate('admin')} data-id="element-653">

            <div className="p-2 bg-emerald-50 text-emerald-600 rounded-full" data-id="element-654">
              <ShieldAlert className="w-5 h-5" data-id="element-655" />
            </div>
            <span className="font-medium" data-id="element-656">Audit Logs</span>
          </Button>
        </div>;
    }
    // Default EF / Executive view
    return <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-657">
        <Button variant="outline" className="h-auto py-4 flex flex-col items-center justify-center gap-2 bg-white hover:bg-slate-50 hover:border-primary/30 transition-all" onClick={() => onNavigate('search')} data-id="element-658">

          <div className="p-2 bg-slate-100 text-slate-600 rounded-full" data-id="element-659">
            <FileSearch className="w-5 h-5" data-id="element-660" />
          </div>
          <span className="font-medium" data-id="element-661">Start a Search</span>
        </Button>
        <Button variant="outline" className="h-auto py-4 flex flex-col items-center justify-center gap-2 bg-white hover:bg-slate-50 hover:border-primary/30 transition-all" onClick={() => onNavigate('matters')} data-id="element-662">

          <div className="p-2 bg-slate-100 text-slate-600 rounded-full" data-id="element-663">
            <Briefcase className="w-5 h-5" data-id="element-664" />
          </div>
          <span className="font-medium" data-id="element-665">Open a Matter</span>
        </Button>
        <Button variant="outline" className="h-auto py-4 flex flex-col items-center justify-center gap-2 bg-white hover:bg-slate-50 hover:border-primary/30 transition-all" onClick={() => onNavigate('workflows')} data-id="element-666">

          <div className="p-2 bg-emerald-50 text-emerald-600 rounded-full" data-id="element-667">
            <Play className="w-5 h-5" data-id="element-668" />
          </div>
          <span className="font-medium" data-id="element-669">Run a Workflow</span>
        </Button>
        <Button variant="outline" className="h-auto py-4 flex flex-col items-center justify-center gap-2 bg-white hover:bg-amber-50 hover:border-amber-300 transition-all border-amber-200" onClick={() => onNavigate('review')} data-id="element-670">

          <div className="p-2 bg-amber-100 text-amber-600 rounded-full" data-id="element-671">
            <ShieldAlert className="w-5 h-5" data-id="element-672" />
          </div>
          <span className="font-medium text-amber-900" data-id="element-673">
            Review Flagged Outputs
          </span>
        </Button>
      </div>;
  };
  return <div className="space-y-6 pb-12" data-id="element-674">
      {/* Header & Global Search */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-675">
        <div data-id="element-676">
          <h1 className="text-2xl font-bold tracking-tight" data-id="element-677">{getGreeting()}</h1>
          <p className="text-muted-foreground" data-id="element-678">{getSubtitle()}</p>
        </div>
        <div className="w-full md:w-96" data-id="element-679">
          <form onSubmit={e => {
          e.preventDefault();
          onNavigate('search');
        }} data-id="element-680">

            <Input placeholder="Ask about a matter, document set, provider, or issue..." icon={<Search className="w-4 h-4" data-id="element-682" />} className="bg-white shadow-sm border-slate-300" data-id="element-681" />

          </form>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-683">
        <Card data-id="element-684">
          <CardContent className="p-4 flex flex-col justify-between h-full" data-id="element-685">
            <div className="flex justify-between items-start mb-2" data-id="element-686">
              <p className="text-xs font-medium text-muted-foreground" data-id="element-687">
                Total Matters
              </p>
              <Briefcase className="w-4 h-4 text-slate-400" data-id="element-688" />
            </div>
            <div data-id="element-689">
              <p className="text-2xl font-bold" data-id="element-690">142</p>
              <p className="text-[10px] text-emerald-600 flex items-center mt-1" data-id="element-691">
                <TrendingUp className="w-3 h-3 mr-1" data-id="element-692" /> +3 this month
              </p>
            </div>
          </CardContent>
        </Card>
        <Card data-id="element-693">
          <CardContent className="p-4 flex flex-col justify-between h-full" data-id="element-694">
            <div className="flex justify-between items-start mb-2" data-id="element-695">
              <p className="text-xs font-medium text-muted-foreground" data-id="element-696">
                Indexed Docs
              </p>
              <Database className="w-4 h-4 text-slate-400" data-id="element-697" />
            </div>
            <div data-id="element-698">
              <p className="text-2xl font-bold" data-id="element-699">48.2k</p>
              <p className="text-[10px] text-muted-foreground mt-1" data-id="element-700">
                Total corpus size
              </p>
            </div>
          </CardContent>
        </Card>
        <Card data-id="element-701">
          <CardContent className="p-4 flex flex-col justify-between h-full" data-id="element-702">
            <div className="flex justify-between items-start mb-2" data-id="element-703">
              <p className="text-xs font-medium text-muted-foreground" data-id="element-704">
                Added (7d)
              </p>
              <Files className="w-4 h-4 text-slate-400" data-id="element-705" />
            </div>
            <div data-id="element-706">
              <p className="text-2xl font-bold" data-id="element-707">1,847</p>
              <p className="text-[10px] text-emerald-600 flex items-center mt-1" data-id="element-708">
                <TrendingUp className="w-3 h-3 mr-1" data-id="element-709" /> +12% vs last week
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50/30" data-id="element-710">
          <CardContent className="p-4 flex flex-col justify-between h-full" data-id="element-711">
            <div className="flex justify-between items-start mb-2" data-id="element-712">
              <p className="text-xs font-medium text-amber-800" data-id="element-713">
                Pending Reviews
              </p>
              <AlertCircle className="w-4 h-4 text-amber-500" data-id="element-714" />
            </div>
            <div data-id="element-715">
              <p className="text-2xl font-bold text-amber-900" data-id="element-716">
                {REVIEW_QUEUE.length}
              </p>
              <p className="text-[10px] text-amber-700 mt-1" data-id="element-717">
                Requires attorney action
              </p>
            </div>
          </CardContent>
        </Card>
        <Card data-id="element-718">
          <CardContent className="p-4 flex flex-col justify-between h-full" data-id="element-719">
            <div className="flex justify-between items-start mb-2" data-id="element-720">
              <p className="text-xs font-medium text-muted-foreground" data-id="element-721">
                Active Chronologies
              </p>
              <Stethoscope className="w-4 h-4 text-blue-500" data-id="element-722" />
            </div>
            <div data-id="element-723">
              <p className="text-2xl font-bold" data-id="element-724">4</p>
              <p className="text-[10px] text-muted-foreground mt-1" data-id="element-725">
                Currently generating
              </p>
            </div>
          </CardContent>
        </Card>
        <Card data-id="element-726">
          <CardContent className="p-4 flex flex-col justify-between h-full" data-id="element-727">
            <div className="flex justify-between items-start mb-2" data-id="element-728">
              <p className="text-xs font-medium text-muted-foreground" data-id="element-729">
                Automation Success
              </p>
              <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-730" />
            </div>
            <div data-id="element-731">
              <p className="text-2xl font-bold" data-id="element-732">98.4%</p>
              <p className="text-[10px] text-muted-foreground mt-1" data-id="element-733">
                Last 30 days
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      {renderQuickActions()}

      {/* Alerts & Exceptions - Only show for EF and RK */}
      {(persona === 'EF' || persona === 'RK') && <Card className="border-red-200 shadow-sm" data-id="element-734">
          <CardHeader className="py-3 bg-red-50/50 border-b border-red-100 flex flex-row items-center justify-between" data-id="element-735">
            <div className="flex items-center space-x-2" data-id="element-736">
              <AlertTriangle className="w-4 h-4 text-red-600" data-id="element-737" />
              <CardTitle className="text-sm font-medium text-red-900" data-id="element-738">
                System Alerts & Exceptions
              </CardTitle>
            </div>
            <Badge variant="destructive" className="text-[10px]" data-id="element-739">
              4 Active
            </Badge>
          </CardHeader>
          <CardContent className="p-0" data-id="element-740">
            <div className="divide-y divide-red-100" data-id="element-741">
              {ALERTS.map(alert => <div key={alert.id} className="p-3 flex items-start space-x-3 hover:bg-slate-50 transition-colors" data-id="element-742">

                  {alert.type === 'error' ? <ServerCrash className="w-4 h-4 text-red-500 mt-0.5 shrink-0" data-id="element-743" /> : alert.type === 'warning' ? <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" data-id="element-744" /> : <AlertCircle className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" data-id="element-745" />}
                  <div className="flex-1 min-w-0" data-id="element-746">
                    <p className="text-sm font-medium text-slate-900" data-id="element-747">
                      {alert.message}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5" data-id="element-748">
                      Matter: {alert.matter}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" className="h-7 text-xs shrink-0" data-id="element-749">

                    Resolve
                  </Button>
                </div>)}
            </div>
          </CardContent>
        </Card>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-750">
        {/* Recent Matters - Show for EF, MS, AT */}
        {(persona === 'EF' || persona === 'MS' || persona === 'AT') && <Card className="lg:col-span-2 shadow-sm" data-id="element-751">
            <CardHeader className="flex flex-row items-center justify-between py-4 border-b" data-id="element-752">
              <div data-id="element-753">
                <CardTitle className="text-base" data-id="element-754">Recent Matters</CardTitle>
                <CardDescription data-id="element-755">
                  Matters with recent activity or updates.
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => onNavigate('matters')} data-id="element-756">

                View All
              </Button>
            </CardHeader>
            <CardContent className="p-0" data-id="element-757">
              <div className="overflow-x-auto" data-id="element-758">
              <table className="w-full text-sm text-left" data-id="element-759">
                <thead className="text-xs text-muted-foreground uppercase bg-muted/50 border-b" data-id="element-760">
                  <tr data-id="element-761">
                    <th className="px-4 py-3 font-medium" data-id="element-762">Matter Name</th>
                    <th className="px-4 py-3 font-medium" data-id="element-763">Status</th>
                    <th className="px-4 py-3 font-medium" data-id="element-764">Last Activity</th>
                  </tr>
                </thead>
                <tbody className="divide-y" data-id="element-765">
                  {MATTERS.slice(0, 4).map(matter => <tr key={matter.id} className="hover:bg-muted/50 transition-colors cursor-pointer group" onClick={() => onNavigate('workspace')} data-id="element-766">

                      <td className="px-4 py-3" data-id="element-767">
                        <p className="font-mono text-[10px] text-muted-foreground mb-0.5" data-id="element-768">
                          {matter.caseNumber}
                        </p>
                        <p className="font-medium text-primary group-hover:underline" data-id="element-769">
                          {matter.name}
                        </p>
                      </td>
                      <td className="px-4 py-3" data-id="element-770">
                        <Badge variant={matter.status.includes('Active') ? 'success' : matter.status.includes('Pre-Litigation') ? 'secondary' : 'warning'} data-id="element-771">

                          {matter.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground text-xs" data-id="element-772">
                        {matter.lastUpdated}
                      </td>
                    </tr>)}
                </tbody>
              </table>
              </div>
            </CardContent>
          </Card>}

        {/* Review Queue Summary - Show for EF, AT */}
        {(persona === 'EF' || persona === 'AT') && <Card className={`shadow-sm border-amber-200 ${persona === 'AT' ? 'lg:col-span-1' : ''}`} data-id="element-773">

            <CardHeader className="py-4 border-b bg-amber-50/30" data-id="element-774">
              <div className="flex justify-between items-center" data-id="element-775">
                <div data-id="element-776">
                  <CardTitle className="text-base flex items-center" data-id="element-777">
                    <CheckSquare className="w-4 h-4 mr-2 text-amber-600" data-id="element-778" />
                    Review Queue
                  </CardTitle>
                  <CardDescription data-id="element-779">Requires attorney approval</CardDescription>
                </div>
                <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => onNavigate('review')} data-id="element-780">

                  <ChevronRight className="w-4 h-4" data-id="element-781" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0" data-id="element-782">
              <div className="divide-y" data-id="element-783">
                {REVIEW_QUEUE.slice(0, 4).map(item => <div key={item.id} className="p-4 hover:bg-muted/30 transition-colors cursor-pointer" onClick={() => onNavigate('review')} data-id="element-784">

                    <div className="flex items-start justify-between mb-1" data-id="element-785">
                      <p className="text-sm font-medium line-clamp-1 pr-2" data-id="element-786">
                        {item.task}
                      </p>
                      <Badge variant={item.aiConfidence > 85 ? 'success' : item.aiConfidence > 70 ? 'warning' : 'destructive'} className="text-[10px] shrink-0" data-id="element-787">

                        {item.aiConfidence}% Conf.
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground truncate mb-1" data-id="element-788">
                      {item.matter}
                    </p>
                    <div className="flex items-center justify-between text-[10px] text-muted-foreground" data-id="element-789">
                      <span className="uppercase tracking-wider" data-id="element-790">
                        {item.type}
                      </span>
                      <span data-id="element-791">{item.date}</span>
                    </div>
                  </div>)}
              </div>
            </CardContent>
          </Card>}

        {/* Ingestion Health - Show for RK instead of Review Queue */}
        {persona === 'RK' && <Card className="shadow-sm lg:col-span-3" data-id="element-792">
            <CardHeader className="py-4 border-b flex flex-row items-center justify-between" data-id="element-793">
              <CardTitle className="text-base flex items-center" data-id="element-794">
                <Activity className="w-4 h-4 mr-2 text-slate-500" data-id="element-795" />
                System & Ingestion Health
              </CardTitle>
              <Button variant="outline" size="sm" onClick={() => onNavigate('admin')} data-id="element-796">

                View Admin Console
              </Button>
            </CardHeader>
            <CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-797">
              <div data-id="element-798">
                <div className="flex justify-between text-sm mb-1" data-id="element-799">
                  <span className="text-muted-foreground" data-id="element-800">
                    OCR Quality (Avg)
                  </span>
                  <span className="font-medium text-emerald-600" data-id="element-801">94.2%</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2" data-id="element-802">
                  <div className="bg-emerald-500 h-2 rounded-full" style={{
                width: '94%'
              }} data-id="element-803">
                </div>
                </div>
              </div>
              <div data-id="element-804">
                <div className="flex justify-between text-sm mb-1" data-id="element-805">
                  <span className="text-muted-foreground" data-id="element-806">
                    Classification Confidence
                  </span>
                  <span className="font-medium text-primary" data-id="element-807">88.7%</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2" data-id="element-808">
                  <div className="bg-primary/60 h-2 rounded-full" style={{
                width: '88%'
              }} data-id="element-809">
                </div>
                </div>
              </div>
              <div data-id="element-810">
                <div className="flex justify-between text-sm mb-1" data-id="element-811">
                  <span className="text-muted-foreground" data-id="element-812">
                    Processing Queue
                  </span>
                  <span className="font-medium text-amber-600" data-id="element-813">142 docs</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2" data-id="element-814">
                  <div className="bg-amber-500 h-2 rounded-full" style={{
                width: '30%'
              }} data-id="element-815">
                </div>
                </div>
              </div>
            </CardContent>
          </Card>}
      </div>

      {/* Show these sections primarily for EF and RK */}
      {(persona === 'EF' || persona === 'RK') && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-816">
          {/* OCR / Ingestion Health - Only if not already shown above */}
          {persona !== 'RK' && <Card className="shadow-sm" data-id="element-817">
              <CardHeader className="py-4 border-b" data-id="element-818">
                <CardTitle className="text-base flex items-center" data-id="element-819">
                  <Activity className="w-4 h-4 mr-2 text-slate-500" data-id="element-820" />
                  Ingestion Health
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4" data-id="element-821">
                <div data-id="element-822">
                  <div className="flex justify-between text-sm mb-1" data-id="element-823">
                    <span className="text-muted-foreground" data-id="element-824">
                      OCR Quality (Avg)
                    </span>
                    <span className="font-medium text-emerald-600" data-id="element-825">94.2%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2" data-id="element-826">
                    <div className="bg-emerald-500 h-2 rounded-full" style={{
                width: '94%'
              }} data-id="element-827">
                </div>
                  </div>
                </div>
                <div data-id="element-828">
                  <div className="flex justify-between text-sm mb-1" data-id="element-829">
                    <span className="text-muted-foreground" data-id="element-830">
                      Classification Confidence
                    </span>
                    <span className="font-medium text-primary" data-id="element-831">88.7%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2" data-id="element-832">
                    <div className="bg-primary/60 h-2 rounded-full" style={{
                width: '88%'
              }} data-id="element-833">
                </div>
                  </div>
                </div>
                <div data-id="element-834">
                  <div className="flex justify-between text-sm mb-1" data-id="element-835">
                    <span className="text-muted-foreground" data-id="element-836">
                      Processing Queue
                    </span>
                    <span className="font-medium text-amber-600" data-id="element-837">142 docs</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2" data-id="element-838">
                    <div className="bg-amber-500 h-2 rounded-full" style={{
                width: '30%'
              }} data-id="element-839">
                </div>
                  </div>
                </div>
              </CardContent>
            </Card>}

          {/* Recent Ingestion Activity */}
          <Card className={`shadow-sm ${persona === 'RK' ? 'lg:col-span-3' : 'lg:col-span-2'}`} data-id="element-840">

            <CardHeader className="py-4 border-b flex flex-row items-center justify-between" data-id="element-841">
              <CardTitle className="text-base" data-id="element-842">
                Recent Ingestion Activity
              </CardTitle>
              <Button variant="ghost" size="sm" className="h-8 text-xs" data-id="element-843">
                View Logs
              </Button>
            </CardHeader>
            <CardContent className="p-0" data-id="element-844">
              <div className="overflow-x-auto" data-id="element-845">
              <table className="w-full text-sm text-left" data-id="element-846">
                <thead className="text-xs text-muted-foreground uppercase bg-muted/50 border-b" data-id="element-847">
                  <tr data-id="element-848">
                    <th className="px-4 py-3 font-medium" data-id="element-849">Batch / Source</th>
                    <th className="px-4 py-3 font-medium" data-id="element-850">Type</th>
                    <th className="px-4 py-3 font-medium" data-id="element-851">Docs</th>
                    <th className="px-4 py-3 font-medium" data-id="element-852">Status</th>
                    <th className="px-4 py-3 font-medium" data-id="element-853">Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y" data-id="element-854">
                  {INGESTION_ACTIVITY.map(activity => <tr key={activity.id} className="hover:bg-muted/50 transition-colors" data-id="element-855">

                      <td className="px-4 py-3 font-medium" data-id="element-856">
                        {activity.batch}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground" data-id="element-857">
                        {activity.type}
                      </td>
                      <td className="px-4 py-3" data-id="element-858">{activity.docs}</td>
                      <td className="px-4 py-3" data-id="element-859">
                        <Badge variant={activity.status === 'Completed' ? 'success' : activity.status === 'Processing' ? 'default' : 'destructive'} className={activity.status === 'Processing' ? 'bg-slate-100 text-slate-800 hover:bg-slate-100' : ''} data-id="element-860">

                          {activity.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground text-xs" data-id="element-861">
                        {activity.time}
                      </td>
                    </tr>)}
                </tbody>
              </table>
              </div>
            </CardContent>
          </Card>
        </div>}

      {/* Show Workflows for EF, RK, MS */}
      {(persona === 'EF' || persona === 'RK' || persona === 'MS') && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-862">
          {/* Workflow Automation Status */}
          <Card className="lg:col-span-2 shadow-sm" data-id="element-863">
            <CardHeader className="py-4 border-b flex flex-row items-center justify-between" data-id="element-864">
              <CardTitle className="text-base flex items-center" data-id="element-865">
                <BrainCircuit className="w-4 h-4 mr-2 text-muted-foreground" data-id="element-866" />
                Workflow Automation Status
              </CardTitle>
              <Button variant="outline" size="sm" onClick={() => onNavigate('workflows')} data-id="element-867">

                Manage
              </Button>
            </CardHeader>
            <CardContent className="p-4 space-y-4" data-id="element-868">
              <div className="flex items-center justify-between p-3 border rounded-lg bg-slate-50/50 border-slate-200" data-id="element-869">
                <div className="flex items-center space-x-3" data-id="element-870">
                  <div className="w-2 h-2 rounded-full bg-primary animate-pulse" data-id="element-871"></div>
                  <div data-id="element-872">
                    <p className="text-sm font-medium" data-id="element-873">
                      Generate Medical Chronology
                    </p>
                    <p className="text-xs text-muted-foreground" data-id="element-874">
                      Nguyen v. St. Luke's Medical Center • Step 2 of 4
                      (Extracting events)
                    </p>
                  </div>
                </div>
                <span className="text-xs font-medium text-primary" data-id="element-875">45%</span>
              </div>
              <div className="flex items-center justify-between p-3 border rounded-lg" data-id="element-876">
                <div className="flex items-center space-x-3" data-id="element-877">
                  <div className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-878"></div>
                  <div data-id="element-879">
                    <p className="text-sm font-medium" data-id="element-880">
                      Bates Label Production
                    </p>
                    <p className="text-xs text-muted-foreground" data-id="element-881">
                      Garcia v. Metro Transit Authority • 452 documents stamped
                    </p>
                  </div>
                </div>
                <span className="text-xs font-medium text-emerald-600" data-id="element-882">
                  Completed
                </span>
              </div>
              <div className="flex items-center justify-between p-3 border rounded-lg" data-id="element-883">
                <div className="flex items-center space-x-3" data-id="element-884">
                  <div className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-885"></div>
                  <div data-id="element-886">
                    <p className="text-sm font-medium" data-id="element-887">
                      Classify Production Set
                    </p>
                    <p className="text-xs text-muted-foreground" data-id="element-888">
                      Castillo v. Westfield Properties • 1,204 documents sorted
                    </p>
                  </div>
                </div>
                <span className="text-xs font-medium text-emerald-600" data-id="element-889">
                  Completed
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Top Document Types */}
          <Card className="shadow-sm" data-id="element-890">
            <CardHeader className="py-4 border-b" data-id="element-891">
              <CardTitle className="text-base flex items-center" data-id="element-892">
                <BarChart3 className="w-4 h-4 mr-2 text-slate-500" data-id="element-893" />
                Corpus Composition
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4" data-id="element-894">
              {DOC_TYPES.map((doc, i) => <div key={i} data-id="element-895">
                  <div className="flex justify-between text-sm mb-1" data-id="element-896">
                    <span className="font-medium" data-id="element-897">{doc.type}</span>
                    <span className="text-muted-foreground" data-id="element-898">
                      {doc.count.toLocaleString()}
                    </span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-1.5" data-id="element-899">
                    <div className="bg-slate-400 h-1.5 rounded-full" style={{
                width: `${doc.percentage}%`
              }} data-id="element-900">
                </div>
                  </div>
                </div>)}
            </CardContent>
          </Card>
        </div>}

      {/* Recently Generated Answers - Show for EF, AT */}
      {(persona === 'EF' || persona === 'AT') && <Card className="shadow-sm" data-id="element-901">
          <CardHeader className="py-4 border-b flex flex-row items-center justify-between" data-id="element-902">
            <div data-id="element-903">
              <CardTitle className="text-base" data-id="element-904">
                Recently Generated Answers
              </CardTitle>
              <CardDescription data-id="element-905">
                Latest grounded responses from the legal knowledge base.
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="p-0" data-id="element-906">
            <div className="divide-y" data-id="element-907">
              {RECENT_ANSWERS.map(answer => <div key={answer.id} className="p-4 hover:bg-muted/30 transition-colors" data-id="element-908">

                  <div className="flex items-start justify-between mb-2" data-id="element-909">
                    <div className="flex items-start space-x-3" data-id="element-910">
                      <div className="mt-0.5 p-1.5 bg-slate-100 text-slate-600 rounded-md" data-id="element-911">
                        <BrainCircuit className="w-4 h-4" data-id="element-912" />
                      </div>
                      <div data-id="element-913">
                        <p className="text-sm font-medium text-foreground" data-id="element-914">
                          "{answer.query}"
                        </p>
                        <p className="text-xs text-muted-foreground mt-1" data-id="element-915">
                          Matter: {answer.matter}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end space-y-2" data-id="element-916">
                      <Badge variant="outline" className="text-[10px] bg-white" data-id="element-917">
                        {answer.confidence}% Confidence
                      </Badge>
                      <span className="text-xs text-muted-foreground" data-id="element-918">
                        {answer.time}
                      </span>
                    </div>
                  </div>
                </div>)}
            </div>
          </CardContent>
          <CardFooter className="p-4 border-t bg-muted/20" data-id="element-919">
            <Button variant="ghost" className="w-full text-sm" onClick={() => onNavigate('search')} data-id="element-920">

              View Search History
            </Button>
          </CardFooter>
        </Card>}
    </div>;
}
function CheckSquare(props: React.SVGProps<SVGSVGElement>) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-921">

      <polyline points="9 11 12 14 22 4" data-id="element-922" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" data-id="element-923" />
    </svg>;
}